import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { decodeShare, type Shared, type SharedEntry } from '@gymcloud/nutrition/share'

import { Working } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { AmountStep } from '~/components/amount-step'
import { EmptyState } from '~/components/empty-state'
import { FoodMark } from '~/components/food-mark'
import { FoodIcon } from '~/components/icons'
import { ListSkeleton } from '~/components/food-search'
import { FoodName } from '~/components/food-name'
import { AlreadyHeldDialog, KeepRider, type HeldAnswer } from '~/components/keeping-shared'
import { MealPage } from '~/components/meal-page'
import { useToast } from '~/components/toast-context'
import { useFoodLogger } from '~/components/use-food-logger'
import { useApi } from '~/data/data-context'
import {
  formatCalories,
  macroLine,
  nowTime,
  recipeFood,
  referenceSaid,
  type Amount,
  type Food,
} from '~/data/nutrition'
import { useMyFoods } from '~/data/food-store'
import { membersOn, type Meal } from '~/data/nutrition'
import { reloadNutrition, useDayEntries, useNutrition } from '~/data/nutrition-store'
import { dayTotal, goalOn } from '~/data/nutrition'
import { todayKey } from '~/data/weighins'
import { withoutDialog } from '~/lib/dialogs'
import { openingAmount } from '~/lib/opening-amount'
import {
  alreadyHeld,
  amountFromShare,
  ingredientsOf,
  keepableOf,
  mealFromShared,
  sameFoodAs,
  readShared,
  type ReadMember,
} from '~/lib/share-food'
import { keepChoicesOf, useKeepChoices } from '~/lib/keeping-shared'

import type { PutFoodBody } from '~/api/endpoints'

/** The foods a link carries whole: the ones there is anything to put in a library. */
function keptFoodsOf(members: readonly ReadMember[]): Food[] {
  return keepableOf(members).flatMap((each) => (each.food === null ? [] : [each.food]))
}

/** A food as the server is asked to keep one: everything the link carried about it. */
function foodBody(food: Food): PutFoodBody {
  return {
    name: food.name,
    emoji: null,
    brand: food.brand,
    barcode: food.barcode,
    reference: food.reference,
    nutrients: food.nutrients,
    portions: food.portions,
    // A copy of a liquid stays a liquid: the density the link carried comes along.
    density: food.density,
  }
}

interface SharedSearch {
  /** The share itself, deflated and written in base64url — the nutrition spec, The link. */
  readonly d?: string
}

export const Route = createFileRoute('/shared')({
  validateSearch: (search: Record<string, unknown>): SharedSearch =>
    typeof search.d === 'string' && search.d !== '' ? { d: search.d } : {},
  component: SharedPage,
})

/** What the page has made of the link so far. */
type Reading =
  | { readonly at: 'reading' }
  | { readonly at: 'unreadable' }
  | { readonly at: 'unresolved' }
  | { readonly at: 'read'; readonly shared: Shared; readonly members: ReadMember[] }

/**
 * A food, a recipe or a meal somebody handed over as a link — the nutrition spec, Opening a
 * shared link.
 *
 * A page of its own rather than a panel over another: an arrival from a chat app has nothing
 * behind it, so there is no page underneath to preserve and the way out leads to the diary.
 * Nothing of what the link carries is drawn until every reference it holds has been read —
 * a meal drawn from half its members is a meal stating the wrong total.
 */
function SharedPage() {
  const { d } = Route.useSearch()
  const api = useApi()
  /*
   * What has been made of the link, and which link it was made of. Kept together so a page
   * handed a second link does not answer for a moment with the first — there is nothing to
   * reset in an effect, because a reading that is not this link's simply does not count.
   */
  const [reading, setReading] = useState<{ of: string | undefined; at: Reading }>({
    of: d,
    at: { at: 'reading' },
  })
  /** Bumped as the link changes, so an answer arriving late for an old one is dropped. */
  const asking = useRef(0)

  useEffect(() => {
    const mine = ++asking.current
    void (async () => {
      const shared = d === undefined ? null : await decodeShare(d)
      if (shared === null) {
        if (asking.current === mine) {
          setReading({ of: d, at: { at: 'unreadable' } })
        }
        return
      }
      try {
        const members = await readShared(shared, (refs) => api.foodsByRef(refs))
        if (asking.current === mine) {
          setReading({
            of: d,
            at: members.some((each) => each.missing)
              ? { at: 'unresolved' }
              : { at: 'read', shared, members },
          })
        }
      } catch {
        if (asking.current === mine) {
          setReading({ of: d, at: { at: 'unresolved' } })
        }
      }
    })()
  }, [d, api])

  // A reading of some other link is no reading of this one.
  const made: Reading = reading.of === d ? reading.at : { at: 'reading' }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      {made.at === 'reading' ? (
        <ListSkeleton label="Opening what was shared" />
      ) : made.at === 'unreadable' ? (
        <EmptyState
          icon={<FoodIcon className="size-8" />}
          title="This link can’t be opened"
          detail="It was damaged on the way, or written by a later version of MixedGrip."
        />
      ) : made.at === 'unresolved' ? (
        <EmptyState
          icon={<FoodIcon className="size-8" />}
          title="This can’t be opened right now"
          detail="One of the foods it refers to could not be read. Check the connection and try again."
        />
      ) : made.shared.k === 'entry' ? (
        <ReceivedEntries shared={made.shared} members={made.members} />
      ) : made.shared.k === 'meal' ? (
        <SharedMeal shared={made.shared} members={made.members} />
      ) : (
        <SharedOneFood shared={made.shared} members={made.members} />
      )}
    </div>
  )
}

/**
 * Who handed it over, and whether they sent or shared it — the nutrition spec, Opening a link.
 * Sent means there are entries here to log; shared means there is a thing here to keep.
 */
function SharedBy({ by, sent = false }: { by: string | undefined; sent?: boolean }) {
  const verb = sent ? 'Sent' : 'Shared'
  return (
    <p className="text-sm text-ink-muted">
      {by === undefined ? `${verb} with you` : `${verb} by ${by}`}
    </p>
  )
}

/**
 * What a blueprint's page offers — the nutrition spec, Opening a blueprint.
 *
 * Keeping leads, because a blueprint is a thing to have: somebody handed it over so it would be
 * in your library, and logging it tonight is the lesser of the two. A library food offers no
 * keeping at all, and then logging is the only thing here and takes the accent itself.
 */
function SharedActions({
  onKeep,
  onLog,
  saving,
  canLog,
  keepReady = true,
}: {
  onKeep: (() => void) | null
  onLog: () => void
  saving: boolean
  canLog: boolean
  /**
   * Whether this device has read what the account already holds. Keeping asks that question
   * before it writes anything, so until it can be answered the key stands down rather than
   * standing ready to make a second of something already there.
   */
  keepReady?: boolean
}) {
  const savingShown = useBeat(saving)
  return (
    <div className="flex flex-wrap justify-end gap-3">
      <button
        type="button"
        disabled={saving || !canLog}
        className={
          onKeep === null
            ? 'rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50'
            : 'rounded-full px-5 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover disabled:opacity-50'
        }
        onClick={onLog}
      >
        Log
      </button>
      {onKeep !== null && (
        <button
          type="button"
          aria-busy={saving || undefined}
          disabled={!saving && !keepReady}
          className="relative rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
          onClick={() => {
            if (!saving) onKeep()
          }}
        >
          <Working shown={savingShown}>Keep in my library</Working>
        </button>
      )}
    </div>
  )
}

/**
 * Entries somebody sent, on the meal's own page — the nutrition spec, Opening an entry share.
 *
 * The same page a meal stands on, because what arrived is a list of foods in amounts and that
 * is what the page is for. What is different is everything about the act: it opens on the
 * moment the link carries rather than on now, its key logs rather than keeps, and it is never
 * kept as itself — the library gains only what the riders were asked for.
 */
function ReceivedEntries({
  shared,
  members,
}: {
  shared: SharedEntry
  members: readonly ReadMember[]
}) {
  const api = useApi()
  const toast = useToast()
  const navigate = useNavigate()
  const data = useNutrition()
  const entries = useDayEntries(shared.dy)
  const mine = useMyFoods()
  const meal = useMemo(() => mealFromShared(shared, members), [shared, members])
  /** The foods the link carries whole, which are the only ones there is anything to keep of. */
  const carried = useMemo(() => keptFoodsOf(members), [members])
  const [keepFoods, setKeepFoods] = useState(false)
  const [makeMeal, setMakeMeal] = useState(false)
  const { choices, setChoices, turn } = useKeepChoices([])

  /*
   * What the account already holds is read once, as the rider is turned on. Held back until
   * this device has read the account: a list marked "you have it" off an unread account would
   * be saying something it does not know.
   */
  const openKeeping = (on: boolean) => {
    setKeepFoods(on)
    if (on && mine !== null) {
      setChoices(keepChoicesOf(carried, mine))
    }
  }

  const riders = (
    <div className="space-y-2">
      {carried.length > 0 && (
        <KeepRider
          label={carried.length === 1 ? 'Keep the food too' : 'Keep the foods too'}
          on={keepFoods}
          onToggle={openKeeping}
          disabled={mine === null}
          choices={choices}
          onChoice={turn}
        />
      )}
      <KeepRider label="Make a meal of it" on={makeMeal} onToggle={setMakeMeal} />
    </div>
  )

  /**
   * What the riders asked for, done once the entries are logged. Never before: keeping a food
   * out of a log that was then not made would leave the library holding something for a meal
   * that was never eaten.
   *
   * The page leaves first. Logging never waits on the server — the nutrition spec, Logging an
   * entry — so what the riders write is sent behind the diary rather than holding the page up.
   */
  const afterLogging = async (landed: string, standing: Meal) => {
    /*
     * The day it landed on, which is not always the day the link carried: the page can be sent
     * to another day before the key is pressed — the nutrition spec, Opening an entry share,
     * "leaves for the diary on that day".
     */
    void navigate({
      to: '/diary',
      search: (held) => ({ ...withoutDialog(held), day: landed }),
    })
    try {
      if (keepFoods) {
        /*
         * Only what is still on the page. A member taken off it was not logged, so keeping the
         * food behind it would put something in the library for a thing nobody ate.
         *
         * Held against the foods themselves rather than their names: one plate can carry two
         * things called the same, and a name left standing by one of them would keep the other.
         */
        const left = membersOn(standing.slots, standing.state).flatMap((each) =>
          each.food === null ? [] : [each.food],
        )
        const standingStill = (food: Food) => left.some((each) => sameFoodAs(each, food))
        for (const choice of choices.filter((each) => each.on && standingStill(each.food))) {
          await api.putFood(crypto.randomUUID(), foodBody(choice.food))
        }
      }
      if (makeMeal) {
        // The meal is what was logged, which is the page as it stood — the nutrition spec.
        await api.putMeal(crypto.randomUUID(), {
          name: standing.name,
          emoji: null,
          slots: membersOn(standing.slots, standing.state).map((member, at) => ({
            id: `kept-${String(at)}`,
            alternatives: [{ id: `kept-${String(at)}a`, member }],
          })),
        })
      }
      if (keepFoods || makeMeal) {
        reloadNutrition()
      }
    } catch {
      toast('Some of that needs a connection.')
    }
  }

  return (
    <MealPage
      key={meal.id}
      meal={meal}
      exists={false}
      day={shared.dy}
      time={shared.tm}
      /*
       * The day it lands on, or null while this device is still reading it — the rings under
       * the head draw what the day already holds, and an unread day drawn as an empty one says
       * nothing was logged on it (`/AGENTS.md`, settled data before settled UI).
       *
       * Only the rings wait. Holding the whole page would sit for ever on a browser that has
       * never managed to read the account at all, which is exactly somebody following a link
       * for the first time.
       */
      entries={entries}
      data={data}
      goal={data === null ? null : goalOn(data.goals, shared.dy)}
      sharedBy={shared.by ?? 'somebody'}
      sentToYou
      /*
       * A plain list, for the same reason the plate is one — the nutrition spec, Sending food.
       * What arrived is what somebody ate, and an alternative to it, or a "not this time", is
       * about a meal being kept and logged again. Something can still be taken off.
       */
      sending={false}
      plainSlots
      riders={riders}
      logLabel="Log this"
      onClose={() => {
        void navigate({ to: '/diary', search: withoutDialog })
      }}
      onLogged={(landed, standing) => {
        void afterLogging(landed, standing)
      }}
    />
  )
}

/**
 * A shared meal, on the meal's own page: a slot per member, to be turned off or changed the
 * way a meal's own are; logged without being kept, or kept as the page stands — the nutrition
 * spec, Opening a blueprint.
 */
function SharedMeal({ shared, members }: { shared: Shared; members: readonly ReadMember[] }) {
  const navigate = useNavigate()
  const today = todayKey()
  const data = useNutrition()
  const entries = useDayEntries(today)
  const meal = useMemo(() => mealFromShared(shared, members), [shared, members])
  return (
    <MealPage
      key={meal.id}
      meal={meal}
      exists={false}
      day={today}
      entries={entries}
      goal={data === null ? null : goalOn(data.goals, today)}
      sharedBy={shared.by ?? 'somebody'}
      onClose={() => {
        void navigate({ to: '/diary', search: withoutDialog })
      }}
      onLogged={() => {
        void navigate({ to: '/diary', search: withoutDialog })
      }}
      // Keeping leaves the shared page for the kept meal's own page.
      onKept={(kept) => {
        void navigate({ to: '/diary/foods', search: { open: `meal:${kept.id}` } })
      }}
    />
  )
}

/**
 * A shared food or recipe: the food itself, an amount of it, and the two things that can be
 * done with it. A library food offers no saving — everybody is offered them already — so the
 * page reads as an ordinary food's page with logging on it.
 */
function SharedOneFood({ shared, members }: { shared: Shared; members: readonly ReadMember[] }) {
  const api = useApi()
  const toast = useToast()
  const navigate = useNavigate()
  const today = todayKey()
  const data = useNutrition()
  const entries = useDayEntries(today)
  const [saving, setSaving] = useState(false)
  const logger = useFoodLogger()
  const mine = useMyFoods()
  /** The one the account already holds that this is, while the dialog about it stands. */
  const [held, setHeld] = useState<Food | null>(null)
  const [keepFoods, setKeepFoods] = useState(false)
  const { choices, setChoices, turn } = useKeepChoices([])

  const ingredients = ingredientsOf(members)
  /*
   * A recipe is worked out from its ingredients the way the app works one out, so what a
   * serving comes to is the same number here as it would be in the library. A plain food is
   * whatever the link carried or the reference read back.
   */
  const food: Food | null =
    shared.k === 'recipe'
      ? recipeFood(
          {
            id: null,
            name: shared.n,
            emoji: null,
            brand: null,
            barcode: null,
          },
          { servings: shared.sv, ingredients, totalGrams: shared.tw ?? null },
        )
      : (members[0]?.food ?? null)

  /*
   * The amount it opens on: what the link offers it in, or a serving of a recipe. Worked out
   * from the food rather than held as state that an effect fills in — what somebody chooses
   * is the only thing here worth remembering.
   */
  const opening =
    food === null
      ? null
      : shared.k === 'recipe'
        ? openingAmount(food, null)
        : amountFromShare(members[0]?.member.a ?? [1, 'g'])
  const [chosen, setChosen] = useState<Amount | null>(null)
  const [time, setTime] = useState(() => nowTime())

  /*
   * A `food` kind whose member is a quick entry — a name and its figures, with no food behind
   * it — reads back as no food at all. The app stopped writing those (they travel as an entry
   * share now), but every one already sent by a shipped build is still out there, and a page
   * that waits for ever on something that will never arrive is the skeleton this rule forbids.
   * Android answers the same way.
   */
  if (food === null || opening === null) {
    return (
      <EmptyState
        icon={<FoodIcon className="size-8" />}
        title="This can’t be opened right now"
        detail="It was written by an older version of MixedGrip, which could not say what it was handing over."
      />
    )
  }
  const amount = chosen ?? opening

  /* Nothing to own where everybody is offered it already — the spec, Opening a blueprint. */
  const ownable = shared.k === 'recipe' || members[0]?.member.c !== undefined
  /*
   * The foods a recipe is made of that nobody else is offered, which are the only ones keeping
   * it could also put in the library. A plain food is itself, and has none.
   */
  const carried = shared.k === 'recipe' ? keptFoodsOf(members) : []

  /*
   * What the account already holds is read as the rider is turned on, and never before: a list
   * marked "you have it" off an account this device has not read would be saying something it
   * does not know.
   */
  const openKeeping = (on: boolean) => {
    setKeepFoods(on)
    if (on && mine !== null) {
      setChoices(keepChoicesOf(carried, mine))
    }
  }

  /** The whole of what keeping writes, so the dialog's answers can each reach for a piece. */
  const asKept = (): PutFoodBody => ({
    ...foodBody(food),
    reference: shared.k === 'recipe' ? 'portion' : food.reference,
    ...(shared.k === 'recipe'
      ? { recipe: { servings: shared.sv, ingredients, totalGrams: shared.tw ?? null } }
      : {}),
  })

  /**
   * Keeping — the nutrition spec, Opening a blueprint. A recipe and a meal are kept without a
   * word; a plain food is first held against what the account already has, since a couple
   * handing the same packet back and forth would otherwise pile up copies of it nobody can
   * tell apart.
   */
  const keep = async () => {
    /*
     * Never off an unread account — `/AGENTS.md`, settled data before settled UI. `mine` is
     * null until this device has read what the account holds, and recognising nothing against
     * an unread library would quietly make a second of something already there. The key stands
     * down until it has settled, so this is the belt to that brace.
     */
    if (mine === null) {
      return
    }
    const already = shared.k === 'recipe' ? null : alreadyHeld(food, mine)
    if (already !== null) {
      setHeld(already)
      return
    }
    await keepAnother()
  }

  /** Keeping it as a new row of its own, whatever else the account holds. */
  const keepAnother = async () => {
    setSaving(true)
    try {
      const id = crypto.randomUUID()
      await api.putFood(id, asKept())
      await keepTheIngredients()
      reloadNutrition()
      toast(`${food.name} kept in your library.`)
      await navigate({ to: '/diary/foods', search: { open: `food:${id}` } })
    } catch {
      setSaving(false)
      toast('That did not reach us. Try again in a moment.')
    }
  }

  /**
   * Taking the figures the link states onto the one already held — the spec, Keeping a food
   * already held. Its nutrients, its portions and its density, and nothing else about it: the
   * name it was given here is the name it goes on having.
   */
  const updateHeld = async (already: Food) => {
    setSaving(true)
    try {
      await api.putFood(already.id ?? crypto.randomUUID(), {
        ...foodBody(already),
        nutrients: food.nutrients,
        portions: food.portions,
        density: food.density,
      })
      reloadNutrition()
      toast(`${already.name} updated.`)
      await navigate({ to: '/diary/foods', search: { open: `food:${already.id ?? ''}` } })
    } catch {
      setSaving(false)
      toast('That did not reach us. Try again in a moment.')
    }
  }

  /** The foods a recipe is made of, where keeping them was asked for. */
  const keepTheIngredients = async () => {
    if (!keepFoods) {
      return
    }
    for (const choice of choices.filter((each) => each.on)) {
      await api.putFood(crypto.randomUUID(), foodBody(choice.food))
    }
  }

  const answerHeld =
    (already: Food) =>
    (answer: HeldAnswer): Promise<void> | undefined => {
      // Keeping works in the dialog, which stays until it is done — the app spec, A press
      // that waits.
      if (answer === 'another') {
        return keepAnother()
      } else if (answer === 'update') {
        return updateHeld(already)
      }
      setHeld(null)
      if (answer === 'open') {
        void navigate({ to: '/diary/foods', search: { open: `food:${already.id ?? ''}` } })
      }
      // Dismissed: the dialog goes and nothing else happens — `/AGENTS.md`.
      return undefined
    }

  /** Logged, and the page leaves in the same press — logging never waits on the server. */
  const log = () => {
    logger.logFood(food, amount, { day: today, time })
    void navigate({ to: '/diary', search: withoutDialog })
  }

  return (
    <div className="space-y-5">
      <div>
        <SharedBy by={shared.by} />
        <h1 className="text-3xl font-semibold">
          <FoodName food={food} />
        </h1>
      </div>
      <div className="flex items-center gap-3">
        <FoodMark food={food} size="size-12 text-2xl" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-ink-muted">
            {formatCalories(food.nutrients.calories)} per {referenceSaid(food)}
          </p>
          <p className="truncate text-sm text-ink-muted">{macroLine(food.nutrients)}</p>
        </div>
      </div>
      <AmountStep
        food={food}
        amount={amount}
        onAmount={setChosen}
        time={time}
        onTime={setTime}
        goal={data === null ? null : goalOn(data.goals, today)}
        dayTotal={entries === null ? null : dayTotal(entries)}
      />
      {/*
        What keeping it would also put in the library, where it is made of foods nobody else is
        offered — the nutrition spec, Opening a blueprint. Off to begin with: the press takes
        the thing that was asked for and nothing more, a recipe being self-contained anyway.
      */}
      {ownable && carried.length > 0 && (
        <KeepRider
          label={
            carried.length === 1 ? 'Keep the food it’s made of' : 'Keep the foods it’s made of'
          }
          on={keepFoods}
          onToggle={openKeeping}
          disabled={mine === null}
          choices={choices}
          onChoice={turn}
        />
      )}
      <SharedActions
        onKeep={
          ownable
            ? () => {
                void keep()
              }
            : null
        }
        keepReady={mine !== null}
        onLog={log}
        saving={saving}
        canLog={amount.value > 0}
      />
      {held !== null && (
        <AlreadyHeldDialog held={held} arriving={food.nutrients} onAnswer={answerHeld(held)} />
      )}
    </div>
  )
}
